/**
 * Mock Ocean Data Service
 * Provides fallback data when ERDDAP is unavailable
 */

import { 
  SeaSurfaceTemperature, 
  ChlorophyllData, 
  SeaSurfaceHeight, 
  CurrentData,
  MixedLayerDepth,
  GeoCoordinate
} from '../types';
import { BoundingBox } from '../types/oceanographic';

export class MockOceanDataService {
  /**
   * Generate mock SST data for Gulf of Mexico
   */
  static generateMockSST(bounds: BoundingBox): SeaSurfaceTemperature[] {
    const data: SeaSurfaceTemperature[] = [];
    const latStep = (bounds.maxLat - bounds.minLat) / 10;
    const lonStep = (bounds.maxLon - bounds.minLon) / 10;
    
    for (let lat = bounds.minLat; lat <= bounds.maxLat; lat += latStep) {
      for (let lon = bounds.minLon; lon <= bounds.maxLon; lon += lonStep) {
        // Gulf of Mexico typical temperatures
        const baseTemp = 25 + (Math.sin((lat - 18) * Math.PI / 17) * 5);
        const variation = Math.random() * 2 - 1;
        
        data.push({
          location: { latitude: lat, longitude: lon },
          temperature: baseTemp + variation,
          timestamp: new Date().toISOString(),
          anomaly: variation
        });
      }
    }
    
    return data;
  }
  
  /**
   * Generate mock Chlorophyll data
   */
  static generateMockChlorophyll(bounds: BoundingBox): ChlorophyllData[] {
    const data: ChlorophyllData[] = [];
    const latStep = (bounds.maxLat - bounds.minLat) / 10;
    const lonStep = (bounds.maxLon - bounds.minLon) / 10;
    
    for (let lat = bounds.minLat; lat <= bounds.maxLat; lat += latStep) {
      for (let lon = bounds.minLon; lon <= bounds.maxLon; lon += lonStep) {
        // Higher chlorophyll near coast and river deltas
        const distFromCoast = Math.min(
          Math.abs(lon - bounds.minLon),
          Math.abs(lon - bounds.maxLon)
        );
        const baseConc = 0.1 + (1 / (1 + distFromCoast * 0.1));
        
        data.push({
          location: { latitude: lat, longitude: lon },
          concentration: baseConc + Math.random() * 0.5,
          timestamp: new Date().toISOString(),
          quality: 'medium' as const
        });
      }
    }
    
    return data;
  }
  
  /**
   * Generate mock SSH data
   */
  static generateMockSSH(bounds: BoundingBox): SeaSurfaceHeight[] {
    const data: SeaSurfaceHeight[] = [];
    const latStep = (bounds.maxLat - bounds.minLat) / 10;
    const lonStep = (bounds.maxLon - bounds.minLon) / 10;
    
    for (let lat = bounds.minLat; lat <= bounds.maxLat; lat += latStep) {
      for (let lon = bounds.minLon; lon <= bounds.maxLon; lon += lonStep) {
        // Loop current influence
        const loopInfluence = lat < 27 ? 0.2 : 0;
        const height = loopInfluence + (Math.random() * 0.4 - 0.2);
        
        data.push({
          location: { latitude: lat, longitude: lon },
          height,
          anomaly: height * 0.5,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    return data;
  }
  
  /**
   * Generate mock Current data
   */
  static generateMockCurrents(bounds: BoundingBox): CurrentData[] {
    const data: CurrentData[] = [];
    const latStep = (bounds.maxLat - bounds.minLat) / 10;
    const lonStep = (bounds.maxLon - bounds.minLon) / 10;
    
    for (let lat = bounds.minLat; lat <= bounds.maxLat; lat += latStep) {
      for (let lon = bounds.minLon; lon <= bounds.maxLon; lon += lonStep) {
        // Loop current flows east in southern Gulf
        const isLoopCurrent = lat < 27 && lon > -90;
        const baseSpeed = isLoopCurrent ? 2.5 : 0.5;
        const direction = isLoopCurrent ? 90 : Math.random() * 360;
        
        data.push({
          location: { latitude: lat, longitude: lon },
          speed: baseSpeed + Math.random() * 0.5,
          direction,
          timestamp: new Date().toISOString(),
          depth: 0
        });
      }
    }
    
    return data;
  }
  
  /**
   * Get all mock ocean data
   */
  static getAllMockData(bounds: BoundingBox) {
    return {
      sst: this.generateMockSST(bounds),
      chlorophyll: this.generateMockChlorophyll(bounds),
      ssh: this.generateMockSSH(bounds),
      currents: this.generateMockCurrents(bounds),
      mld: [] as MixedLayerDepth[],
      metadata: {
        source: 'Mock Data (ERDDAP Unavailable)',
        resolution: 0.1,
        lastUpdated: new Date(),
        region: bounds
      }
    };
  }
}